import { useState } from "react";

import { useDeleteProject } from "../../hooks/useProjects";

interface Props {
  projectId: string;
  projectName: string;
  /** Summary counts shown in the warning copy — omit if unknown. */
  fileCount?: number;
  studyCount?: number;
  runCount?: number;
  hasBoundary?: boolean;
  onClose: () => void;
  /** Called after a successful hard delete (DB + S3 cascade). */
  onDeleted: () => void;
}

/**
 * "Delete forever" confirmation modal.
 *
 * GitHub/Linear pattern: the user must type the project name verbatim
 * to enable the destructive button. Prevents accidental deletion when
 * a click slip happens.
 *
 * The modal calls ``DELETE /api/projects/{id}`` (no ``?soft=true``) and
 * navigates away via ``onDeleted`` on success. TanStack Query handles
 * cache invalidation in :func:`useDeleteProject`.
 */
export function DeleteProjectModal({
  projectId,
  projectName,
  fileCount,
  studyCount,
  runCount,
  hasBoundary,
  onClose,
  onDeleted,
}: Props) {
  const [typed, setTyped] = useState("");
  const mutation = useDeleteProject();
  const matches = typed.trim() === projectName;

  const onConfirm = async () => {
    if (!matches) return;
    try {
      await mutation.mutateAsync(projectId);
      onDeleted();
    } catch {
      // mutation.isError surfaces below
    }
  };

  const summaryParts: string[] = [];
  if (fileCount !== undefined) {
    summaryParts.push(`${fileCount} file${fileCount === 1 ? "" : "s"}`);
  }
  if (studyCount !== undefined) {
    summaryParts.push(`${studyCount} stud${studyCount === 1 ? "y" : "ies"}`);
  }
  if (runCount !== undefined) {
    summaryParts.push(`${runCount} run${runCount === 1 ? "" : "s"}`);
  }
  if (hasBoundary) {
    summaryParts.push("the uploaded boundary");
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-project-title"
      data-testid="delete-project-modal"
      style={styles.backdrop}
    >
      <div style={styles.card}>
        <h2 id="delete-project-title" style={styles.title}>
          Delete this project?
        </h2>
        <p style={styles.body}>
          <strong>{projectName}</strong>
          {summaryParts.length > 0 && (
            <>
              {" "}
              and {summaryParts.join(", ")} will be permanently removed.
            </>
          )}{" "}
          This cannot be undone.
        </p>

        <label style={styles.label} htmlFor="delete-confirm-input">
          Type the project name to confirm: <code>{projectName}</code>
        </label>
        <input
          id="delete-confirm-input"
          data-testid="delete-confirm-input"
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoFocus
          style={styles.input}
          autoComplete="off"
          spellCheck={false}
        />

        {mutation.isError && (
          <div role="alert" data-testid="delete-error" style={styles.error}>
            Couldn't delete the project. Please retry, or contact support if it
            keeps failing.
          </div>
        )}

        <div style={styles.actions}>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            data-testid="delete-cancel"
            style={styles.cancelLink}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void onConfirm()}
            disabled={!matches || mutation.isPending}
            data-testid="delete-confirm-button"
            style={matches && !mutation.isPending ? styles.danger : styles.dangerDisabled}
          >
            {mutation.isPending ? "Deleting…" : "Delete forever"}
          </button>
        </div>
      </div>
    </div>
  );
}

const DANGER = "#dc2626";
const DANGER_HOVER = "#b91c1c";

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 50,
    padding: "1rem",
  },
  card: {
    width: "100%",
    maxWidth: 480,
    background: "#ffffff",
    borderRadius: 12,
    padding: "1.75rem",
    boxShadow:
      "0 0 0 1px rgba(50, 50, 93, 0.04), 0 18px 36px -8px rgba(15, 23, 42, 0.18)",
  },
  title: {
    fontSize: "1.125rem",
    fontWeight: 600,
    letterSpacing: "-0.01em",
    margin: "0 0 0.75rem",
    color: "#0a0e27",
  },
  body: {
    fontSize: "0.9375rem",
    color: "#475569",
    lineHeight: 1.55,
    margin: "0 0 1rem",
  },
  label: {
    display: "block",
    fontSize: "0.8125rem",
    color: "#475569",
    margin: "0 0 0.375rem",
  },
  input: {
    width: "100%",
    padding: "0.5rem 0.75rem",
    border: "1px solid #cbd5e1",
    borderRadius: 6,
    fontSize: "0.9375rem",
    fontFamily: "inherit",
    marginBottom: "1rem",
  },
  error: {
    background: "#fff7f5",
    color: "#9a3412",
    border: "1px solid #fed7aa",
    borderRadius: 6,
    padding: "0.625rem 0.75rem",
    fontSize: "0.8125rem",
    marginBottom: "0.75rem",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "1rem",
  },
  cancelLink: {
    background: "transparent",
    color: "#475569",
    border: "none",
    padding: "0.5rem 0.5rem",
    fontSize: "0.9375rem",
    cursor: "pointer",
  },
  danger: {
    background: DANGER,
    color: "#ffffff",
    border: "none",
    padding: "0.5rem 1rem",
    borderRadius: 6,
    fontSize: "0.9375rem",
    fontWeight: 500,
    cursor: "pointer",
    transition: "background 120ms ease",
  },
  dangerDisabled: {
    background: DANGER_HOVER,
    color: "#ffffff",
    border: "none",
    padding: "0.5rem 1rem",
    borderRadius: 6,
    fontSize: "0.9375rem",
    fontWeight: 500,
    opacity: 0.5,
    cursor: "not-allowed",
  },
};
